import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import publicApi from '../services/publicApi';

const BRAND_BLUE = '#1e3a5f';

export default function VerifyPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Hash check state
  const [hashChecking, setHashChecking] = useState(false);
  const [hashResult, setHashResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [showTsaModal, setShowTsaModal] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    publicApi.get(`/forms-public/verify/${id}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.message || 'Document niet gevonden'))
      .finally(() => setLoading(false));
  }, [id]);

  const fmtDate = (d) => d ? new Date(d).toLocaleString('nl-NL', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  }) : '-';

  const statusKleur = (s) => ({
    concept:     { bg: '#fef3c7', fg: '#92400e', label: 'Concept' },
    ingediend:   { bg: '#dbeafe', fg: '#1e40af', label: 'Ingediend' },
    goedgekeurd: { bg: '#d1fae5', fg: '#065f46', label: 'Goedgekeurd' },
    afgewezen:   { bg: '#fee2e2', fg: '#991b1b', label: 'Afgewezen' }
  })[s] || { bg: '#f1f5f9', fg: '#475569', label: s };

  // Bereken SHA-256 hash van een File via Web Crypto API
  const hashFile = async (file) => {
    const buffer = await file.arrayBuffer();
    const hashBuf = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuf))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setHashResult({ type: 'error', message: 'Upload alstublieft een PDF-bestand.' });
      return;
    }
    setHashChecking(true);
    setHashResult(null);
    try {
      const uploaded_hash = await hashFile(file);
      const r = await publicApi.post(`/forms-public/verify/${id}/check`, { uploaded_hash });
      const res = r.data;
      if (res.match === true) {
        setHashResult({ type: 'match', message: res.message, when: res.pdf_hash_at });
      } else if (res.match === false) {
        setHashResult({ type: 'mismatch', message: res.message });
      } else {
        setHashResult({ type: 'unavailable', message: res.message });
      }
    } catch (e) {
      setHashResult({ type: 'error', message: e.response?.data?.message || 'Verificatie mislukt' });
    } finally {
      setHashChecking(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const wrapper = (inner) => (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', padding: 20, fontFamily: 'system-ui, -apple-system, Arial, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ maxWidth: 520, width: '100%', background: 'white', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {inner}
      </div>
    </div>
  );

  if (loading) return wrapper(
    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
      Document verifieren…
    </div>
  );

  if (error || !data?.authentiek) return wrapper(
    <>
      <div style={{ background: '#dc2626', color: 'white', padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>⚠️</div>
        <h2 style={{ margin: 0, fontSize: 22 }}>Niet geverifieerd</h2>
      </div>
      <div style={{ padding: 24, color: '#475569', textAlign: 'center' }}>
        <p style={{ margin: '0 0 12px' }}>{error || 'Dit document kon niet worden geverifieerd.'}</p>
        <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>Document ID: {id}</p>
      </div>
    </>
  );

  const k = statusKleur(data.status);

  // Hash check resultaat-blok
  const renderHashResult = () => {
    if (!hashResult) return null;
    const styles = {
      match:        { bg: '#f0fdf4', border: '#10b981', icon: '✓', titleColor: '#166534' },
      mismatch:     { bg: '#fef2f2', border: '#dc2626', icon: '✗', titleColor: '#991b1b' },
      unavailable:  { bg: '#eff6ff', border: '#3b82f6', icon: 'ℹ', titleColor: '#1e40af' },
      error:        { bg: '#fef2f2', border: '#dc2626', icon: '⚠', titleColor: '#991b1b' }
    }[hashResult.type];
    const titles = {
      match: 'Document is ongewijzigd',
      mismatch: 'Document is aangepast',
      unavailable: 'Verificatie niet beschikbaar',
      error: 'Fout bij verificatie'
    };
    return (
      <div style={{ marginTop: 12, padding: 14, background: styles.bg, border: `1.5px solid ${styles.border}`, borderRadius: 8 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ fontSize: 22, lineHeight: 1, color: styles.border, fontWeight: 700 }}>{styles.icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: styles.titleColor, marginBottom: 2, fontSize: 14 }}>{titles[hashResult.type]}</div>
            <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>{hashResult.message}</div>
            {hashResult.when && (
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                Originele hash berekend op: {fmtDate(hashResult.when)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return wrapper(
    <>
      <div style={{ background: '#10b981', color: 'white', padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
        <h2 style={{ margin: 0, fontSize: 22 }}>Document geverifieerd</h2>
        <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>Authentiek WOMsys Forms document</div>
      </div>

      <div style={{ padding: 24 }}>
        <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
              <td style={{ padding: '12px 0', color: '#64748b', width: '40%' }}>Formulier</td>
              <td style={{ padding: '12px 0', fontWeight: 600 }}>{data.template_naam}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
              <td style={{ padding: '12px 0', color: '#64748b' }}>Organisatie</td>
              <td style={{ padding: '12px 0', fontWeight: 600 }}>{data.bedrijf_naam}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
              <td style={{ padding: '12px 0', color: '#64748b' }}>Document ID</td>
              <td style={{ padding: '12px 0', fontWeight: 600 }}>#{data.submission_id}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
              <td style={{ padding: '12px 0', color: '#64748b' }}>Status</td>
              <td style={{ padding: '12px 0' }}>
                <span style={{ background: k.bg, color: k.fg, padding: '4px 10px', borderRadius: 4, fontSize: 13, fontWeight: 600 }}>{k.label}</span>
              </td>
            </tr>
            {data.ingediend_op && (
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '12px 0', color: '#64748b' }}>Ingediend op</td>
                <td style={{ padding: '12px 0', fontWeight: 600 }}>{fmtDate(data.ingediend_op)}</td>
              </tr>
            )}
            {data.aantal_handtekeningen > 0 && (
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '12px 0', color: '#64748b' }}>Ondertekend</td>
                <td style={{ padding: '12px 0', fontWeight: 600 }}>
                  ✍️ {data.aantal_handtekeningen} handtekening{data.aantal_handtekeningen > 1 ? 'en' : ''}
                  {data.laatste_ondertekening && (
                    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400, marginTop: 2 }}>
                      Laatste: {fmtDate(data.laatste_ondertekening)}
                    </div>
                  )}
                </td>
              </tr>
            )}
            {data.tsa_timestamp_at && (
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '12px 0', color: '#64748b' }}>🔒 RFC 3161 Tijdstempel</td>
                <td style={{ padding: '12px 0', fontWeight: 600 }}>
                  {fmtDate(data.tsa_timestamp_at)}
                  <button onClick={() => setShowTsaModal(true)}
                    style={{ marginLeft: 8, background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 8px', fontSize: 12, color: '#475569', cursor: 'pointer' }}>
                    ℹ Wat is dit?
                  </button>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400, marginTop: 2 }}>
                    Onafhankelijk vastgelegd door FreeTSA · eIDAS Trede 2
                  </div>
                </td>
              </tr>
            )}
            <tr>
              <td style={{ padding: '12px 0', color: '#64748b' }}>Geverifieerd op</td>
              <td style={{ padding: '12px 0', fontSize: 13, color: '#64748b' }}>{fmtDate(data.geverifieerd_op)}</td>
            </tr>
          </tbody>
        </table>

        {showTsaModal && data.tsa_timestamp_at && (
          <div style={{position:'fixed', inset:0, background:'rgba(15,23,42,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16}} onClick={() => setShowTsaModal(false)}>
            <div style={{background:'white', maxWidth:500, padding:24, borderRadius:8, boxShadow:'0 10px 40px rgba(0,0,0,0.2)'}} onClick={e => e.stopPropagation()}>
              <h3 style={{margin:'0 0 12px 0', fontSize:18}}>🔒 RFC 3161 Tijdstempel</h3>
              <p style={{lineHeight:1.6, color:'#475569', fontSize:14, margin:'0 0 12px 0'}}>
                Dit document is op <strong>{fmtDate(data.tsa_timestamp_at)}</strong> onafhankelijk vastgelegd bij <strong>FreeTSA</strong> volgens de RFC 3161 standaard.
              </p>
              <p style={{lineHeight:1.6, color:'#475569', fontSize:14, margin:'0 0 8px 0'}}>
                Een Time Stamp Authority (TSA) is een vertrouwde derde partij die digitaal bewijst dat een document op een specifiek moment bestond. Hierdoor:
              </p>
              <ul style={{lineHeight:1.6, color:'#475569', fontSize:14, paddingLeft:20, marginTop:0}}>
                <li>Kan niemand achteraf beweren dat het document later is aangemaakt</li>
                <li>Is wijziging na deze datum direct detecteerbaar (zie hash-controle hieronder)</li>
                <li>Voldoet aan eIDAS Trede 2 (AES) eisen</li>
              </ul>
              <button onClick={() => setShowTsaModal(false)} style={{marginTop:8, padding:'8px 16px', background:'#3b82f6', color:'white', border:'none', borderRadius:6, cursor:'pointer', fontWeight:600}}>Sluiten</button>
            </div>
          </div>
        )}
        {/* HASH CHECK SECTIE */}
        <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>
            🔍 Controleer of de PDF is gewijzigd
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12, lineHeight: 1.5 }}>
            Upload het PDF-document om te controleren of het exact overeenkomt met het origineel zoals opgeslagen in WOMsys.
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragActive ? '#3b82f6' : '#cbd5e0'}`,
              background: dragActive ? '#eff6ff' : '#f8fafc',
              borderRadius: 8,
              padding: 20,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.15s'
            }}
          >
            {hashChecking ? (
              <div style={{ color: '#3b82f6', fontWeight: 600, fontSize: 14 }}>
                Hash berekenen…
              </div>
            ) : (
              <>
                <div style={{ fontSize: 28, marginBottom: 4 }}>📄</div>
                <div style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                  Klik om PDF te kiezen of sleep er één in
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  De PDF wordt lokaal in je browser gehasht — niet geüpload
                </div>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>

          {renderHashResult()}
        </div>

        <div style={{ marginTop: 20, padding: 14, background: '#f0fdf4', borderRadius: 8, fontSize: 13, color: '#166534', lineHeight: 1.5 }}>
          <strong>🔒 Privacy:</strong> Deze pagina toont alleen authenticiteits-metadata. De inhoud van het formulier is niet zichtbaar zonder toegangsrechten.
        </div>

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e2e8f0', textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>
          Verzonden via <strong style={{ color: BRAND_BLUE }}>WOMsys Forms</strong> · forms.womsys.nl
        </div>
      </div>
    </>
  );
}
